// Pipe Take-off — project list. Lists the current user's takeoff projects
// (admin sees only their own here; the admin centre's user view doesn't
// cross-project yet — Phase 5 polish). Row markup lives in
// ProjectListRow.tsx to keep this file under the 150-line cap.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Plus } from "lucide-react";
import { api } from "../../../lib/api";
import { ProjectListRow, type ProjectRowDto } from "./ProjectListRow";

export function ProjectList() {
  const [rows, setRows] = useState<ProjectRowDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<{ projects: ProjectRowDto[] }>(
          "/api/tools/pipe-takeoff/projects",
        );
        setRows(r.projects);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const deleteProject = async (id: number, name: string) => {
    if (
      !window.confirm(
        `Delete project "${name}"? This removes the project, items, and any uploaded PDF. Cannot be undone.`,
      )
    ) return;
    setDeletingId(id);
    setError(null);
    try {
      await api(`/api/tools/pipe-takeoff/projects/${id}`, { method: "DELETE" });
      setRows((current) => (current ?? []).filter((r) => r.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div
        className="bsp-page-header"
        style={{ justifyContent: "space-between" }}
      >
        <h1 className="bsp-page-title">Pipe Spool Take-off</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/tools/pipe-takeoff/references" className="bsp-btn bsp-btn-secondary">
            Reference library
          </Link>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={() => navigate("/tools/pipe-takeoff/new")}
          >
            <Plus size={14} style={{ marginRight: 6 }} />
            New project
          </button>
        </div>
      </div>

      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)" }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}

      {rows === null && !error && <p>Loading projects…</p>}

      {rows && rows.length === 0 && (
        <p style={{ color: "var(--bsp-fg-muted)" }}>
          No projects yet. Click <strong>New project</strong> to upload a PDF.
        </p>
      )}

      {rows && rows.length > 0 && (
        <div className="bsp-card" style={{ padding: 0 }}>
          <table className="bsp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Drawing</th>
                <th>PDF</th>
                <th>Status</th>
                <th>Created</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ProjectListRow
                  key={row.id}
                  row={row}
                  deleting={deletingId === row.id}
                  onDelete={deleteProject}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
